import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const diseases = [
    {
      icon: 'ri-heart-pulse-line',
      title: 'Familial Hypertrophic Cardiomyopathy (HCM)',
      description: 'A condition characterised by thickening of the heart muscle, especially the left ventricle.',
      symptoms: 'Can lead to irregular heartbeats, fainting, or sudden cardiac arrest, especially in young athletes.',
      genes: 'The main genes involved are MYH7 and MYBPC3.',
      inheritance: 'Usually autosomal dominant, there\'s a 50% chance a child will inherit it.'
    },
    {
      icon: 'ri-heart-line',
      title: 'Familial Dilated Cardiomyopathy (DCM)',
      description: 'A condition where the heart\'s main pumping chamber becomes enlarged and weak.',
      symptoms: 'Can lead to heart failure and arrhythmias.',
      genes: 'The key genes involved are LMNA, TTN, and MYH7.',
      inheritance: 'Often affects multiple generations.'
    },
    {
      icon: 'ri-pulse-line',
      title: 'Familial Arrhythmogenic Right Ventricular Cardiomyopathy (ARVC)',
      description: 'A disorder where the heart muscle, especially the right ventricle, is replaced by fatty or fibrous tissue.',
      symptoms: 'Can cause life-threatening arrhythmias and sudden cardiac death, particularly during exercise.',
      genes: 'The primary genes involved are PKP2, DSP, DSG2, DSC2, and JUP.',
      inheritance: ''
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Familial Long QT Syndrome (LQTS)',
      description: 'A genetic disorder that affects the heart\'s electrical system, leading to abnormal heart rhythms.',
      symptoms: 'Can cause fainting, seizures, or sudden cardiac death, especially during exercise or emotional stress.',
      genes: 'The main genes involved include KCNQ1, KCNH2, and SCN5A.',
      inheritance: ''
    },
    {
      icon: 'ri-medicine-bottle-line',
      title: 'Familial Hypercholesterolemia (FH)',
      description: 'A genetic disorder causing very high LDL ("bad") cholesterol from birth.',
      symptoms: 'Cholesterol builds up in arteries early, leading to early heart attacks or stroke.',
      genes: 'The key genes involved are LDLR, APOB, and PCSK9.',
      inheritance: 'Affects approximately 1 in 250 people worldwide.'
    },
    {
      icon: 'ri-heart-add-line',
      title: 'Familial Aortic Aneurysm & Dissection',
      description: 'A condition involving weakening of the aorta (main artery from the heart).',
      symptoms: 'The aorta can enlarge or tear (dissect) — a medical emergency.',
      genes: 'The primary genes involved are FBN1 (Marfan syndrome), TGFBR1, and TGFBR2.',
      inheritance: ''
    }
  ];

  // Calculate max slide based on screen size
  const getMaxSlide = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 1024) {
        return diseases.length - 1; // Mobile: show 1 card at a time
      }
      return Math.max(0, diseases.length - 3); // Desktop: show 3 cards
    }
    return Math.max(0, diseases.length - 3);
  };

  const [maxSlide, setMaxSlide] = useState(getMaxSlide());

  useEffect(() => {
    const handleResize = () => {
      setMaxSlide(getMaxSlide());
      setCurrentSlide(0); // Reset to first slide on resize
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => {
        if (prev >= maxSlide) {
          return 0;
        }
        return prev + 1;
      });
    }, 4000);

    return () => clearInterval(timer);
  }, [maxSlide]);

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => {
      if (prev <= 0) {
        return maxSlide;
      }
      return prev - 1;
    });
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => {
      if (prev >= maxSlide) {
        return 0;
      }
      return prev + 1;
    });
  };

  // Calculate transform based on screen size
  const getTransform = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      // Mobile: each card is 100% wide + gap (gap-4 = 16px)
      return `translateX(calc(-${currentSlide * 100}% - ${currentSlide * 16}px))`;
    }
    // Desktop: each card is 1/3 width + gap (gap-6 = 24px)
    return `translateX(calc(-${currentSlide * (100 / 3)}% - ${currentSlide * 24}px))`;
  };

  return (
    <>
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 px-4 sm:px-6" style={{ background: 'linear-gradient(135deg, #fef3f2 0%, #fde8e7 50%, #fdd8d6 100%)' }}>
        {/* Decorative curved line */}
        <div className="absolute top-0 right-0 w-1/2 h-full overflow-hidden pointer-events-none">
          <svg className="absolute top-0 right-0 w-full h-full" viewBox="0 0 800 800" preserveAspectRatio="none">
            <path d="M 800,0 Q 600,400 800,800 L 800,0 Z" fill="none" stroke="#f04e58" strokeWidth="2" opacity="0.3"/>
          </svg>
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-0 sm:px-6 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-normal text-gray-900 mb-6 sm:mb-8 leading-tight">
                Genetic Heart Risk Begins Early
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl text-gray-700 mb-8 sm:mb-10 leading-relaxed font-light max-w-xl mx-auto lg:mx-0">
                With Vazcular – Get "Predictive, Proactive, Preventive & Personalized" care
              </p>
              
              <Link
                to="/consultation"
                className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-[#0d9488] text-white rounded-lg font-medium hover:bg-[#0f766e] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                Book a Free Consultation
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>

            {/* Right Image */}
            <div className="relative mt-8 lg:mt-0">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://static.readdy.ai/image/ff0b7e80ce034da2f0ffbed7fce4b50d/c9943c2e7e2e968614f09f4dcae98db1.png"
                  alt="Doctor with family representing comprehensive care"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is Familial Heart Disease Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-gray-900 mb-4 sm:mb-6">
                What is <strong className="font-semibold text-[#f04e58]">Familial Heart Disease?</strong>
              </h2>
              
              <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 leading-relaxed font-light">
                Familial heart diseases are inherited conditions that increase a person's risk of developing heart problems — often at a younger age than usual.
              </p>
              
              <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 leading-relaxed font-light">
                They occur because of genetic mutations passed from parents to children that affect the heart's structure, rhythm, or how it processes fats like cholesterol.
              </p>

              {/* Key Points */}
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0d9488]/10 flex-shrink-0">
                    <i className="ri-dna-line text-xl sm:text-2xl text-[#0d9488]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">Genetic Inheritance</h3>
                    <p className="text-gray-600 font-light">Passed down through families via genetic mutations</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0d9488]/10 flex-shrink-0">
                    <i className="ri-time-line text-xl sm:text-2xl text-[#0d9488]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">Early Onset</h3>
                    <p className="text-gray-600 font-light">Can affect people at younger ages than typical heart disease</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0d9488]/10 flex-shrink-0">
                    <i className="ri-heart-pulse-line text-xl sm:text-2xl text-[#0d9488]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">Multiple Impacts</h3>
                    <p className="text-gray-600 font-light">Affects heart structure, rhythm, and cholesterol processing</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative mt-8 lg:mt-0">
              <div className="relative rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/0032c8811d33e43cc078f1bff7065523.jpg"
                  alt="Familial heart disease genetic concept"
                  className="w-full h-auto object-cover"
                />
              </div>

              {/* Floating Stats Card - Hidden on mobile, repositioned on tablet+ */}
              <div className="hidden sm:block absolute -bottom-4 sm:-bottom-6 lg:-bottom-8 -left-4 sm:-left-6 lg:-left-8 bg-white rounded-2xl shadow-2xl p-4 sm:p-6 max-w-xs">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-full bg-[#f04e58]/10 flex-shrink-0">
                    <i className="ri-shield-check-line text-xl sm:text-2xl text-[#f04e58]"></i>
                  </div>
                  <div>
                    <p className="text-2xl sm:text-3xl font-bold text-gray-900">50%</p>
                    <p className="text-xs sm:text-sm text-gray-600 font-light">Inheritance risk from affected parent</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Animated Sliding Section - Types of Familial Heart Diseases */}
      <section className="py-12 sm:py-16 lg:py-32 bg-gradient-to-br from-[#fef3f2] via-white to-[#fde8e7] overflow-hidden relative">
        {/* Decorative Background Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#f04e58]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#0d9488]/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-10 sm:mb-14 lg:mb-20">
            <div className="inline-block mb-4">
              <span className="px-3 sm:px-4 py-2 bg-[#f04e58]/10 text-[#f04e58] rounded-full text-xs sm:text-sm font-semibold">
                Medical Conditions
              </span>
            </div>
            <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-gray-900 mb-4 sm:mb-6 leading-tight px-4">
              Types of <strong className="font-semibold text-[#f04e58]">Familial Heart Diseases</strong>
            </h2>
            <p className="text-sm sm:text-lg lg:text-xl text-gray-600 font-light max-w-3xl mx-auto leading-relaxed px-4">
              Understanding the different inherited heart conditions and their genetic patterns
            </p>
          </div>

          {/* Sliding Cards Container */}
          <div className="relative px-8 sm:px-12 lg:px-20">
            <div className="overflow-hidden rounded-2xl sm:rounded-3xl">
              <div 
                className="flex gap-4 sm:gap-6 transition-transform duration-700 ease-in-out py-2 sm:py-4"
                style={{ transform: getTransform() }}
              >
                {diseases.map((disease, index) => (
                  <div key={index} className="w-full lg:w-1/3 flex-shrink-0">
                    <div className="bg-white rounded-2xl sm:rounded-3xl border border-gray-100 p-5 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-500 h-full min-h-[400px] sm:min-h-[460px] lg:min-h-[520px] hover:-translate-y-2 group">
                      {/* Icon and Title */}
                      <div className="flex flex-col items-center gap-3 sm:gap-5 mb-5 sm:mb-6">
                        <div className="w-14 h-14 sm:w-20 sm:h-20 flex items-center justify-center rounded-2xl bg-gradient-to-br from-[#f04e58]/10 to-[#f04e58]/5 flex-shrink-0 group-hover:scale-110 transition-transform duration-500">
                          <i className={`${disease.icon} text-2xl sm:text-4xl text-[#f04e58]`}></i>
                        </div>
                        <h3 className="text-base sm:text-xl font-bold text-gray-900 text-center leading-tight">
                          {disease.title}
                        </h3>
                      </div>

                      {/* Content */}
                      <div className="space-y-3 sm:space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0 group-hover:scale-150 transition-transform"></div>
                          <p className="text-xs sm:text-sm text-gray-700 leading-relaxed font-light">
                            {disease.description}
                          </p>
                        </div>

                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0 group-hover:scale-150 transition-transform"></div>
                          <p className="text-xs sm:text-sm text-gray-700 leading-relaxed font-light">
                            {disease.symptoms}
                          </p>
                        </div>

                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0 group-hover:scale-150 transition-transform"></div>
                          <p className="text-xs sm:text-sm text-gray-700 leading-relaxed font-light">
                            {disease.genes}
                          </p>
                        </div>

                        {disease.inheritance && (
                          <div className="flex items-start gap-3">
                            <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0 group-hover:scale-150 transition-transform"></div>
                            <p className="text-xs sm:text-sm text-gray-700 leading-relaxed font-light">
                              {disease.inheritance}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Bottom Accent Line */}
                      <div className="mt-5 sm:mt-6 pt-4 sm:pt-5 border-t border-gray-100">
                        <div className="w-16 h-1 bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-full mx-auto group-hover:w-full transition-all duration-500"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Dots */}
            <div className="flex justify-center gap-2 sm:gap-3 mt-8 sm:mt-12 lg:mt-16">
              {Array.from({ length: maxSlide + 1 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-2 rounded-full transition-all duration-500 cursor-pointer ${
                    currentSlide === index 
                      ? 'bg-gradient-to-r from-[#f04e58] to-[#0d9488] w-8 sm:w-12 shadow-lg' 
                      : 'bg-gray-300 w-2 hover:bg-gray-400 hover:w-4'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            {/* Navigation Arrows - visible on all sizes but smaller on mobile */}
            <button
              onClick={handlePrevSlide}
              className="absolute left-0 top-[45%] -translate-y-1/2 w-8 h-8 sm:w-12 sm:h-12 lg:w-14 lg:h-14 flex items-center justify-center rounded-full bg-white shadow-xl hover:shadow-2xl hover:bg-[#f04e58] hover:text-white transition-all duration-300 cursor-pointer group border border-gray-100"
              aria-label="Previous slide"
            >
              <i className="ri-arrow-left-s-line text-lg sm:text-2xl lg:text-3xl text-gray-700 group-hover:text-white transition-colors"></i>
            </button>

            <button
              onClick={handleNextSlide}
              className="absolute right-0 top-[45%] -translate-y-1/2 w-8 h-8 sm:w-12 sm:h-12 lg:w-14 lg:h-14 flex items-center justify-center rounded-full bg-white shadow-xl hover:shadow-2xl hover:bg-[#f04e58] hover:text-white transition-all duration-300 cursor-pointer group border border-gray-100"
              aria-label="Next slide"
            >
              <i className="ri-arrow-right-s-line text-lg sm:text-2xl lg:text-3xl text-gray-700 group-hover:text-white transition-colors"></i>
            </button>
          </div>
        </div>
      </section>

      {/* What is Familial Hypercholesterolemia Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            {/* Left - Image Grid */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="rounded-2xl overflow-hidden shadow-lg h-48 sm:h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=Diverse%20multi-generational%20family%20group%20portrait%20with%20elderly%20grandparents%2C%20middle-aged%20parents%2C%20and%20young%20children%2C%20standing%20together%20outdoors%2C%20warm%20natural%20lighting%2C%20happy%20smiles%2C%20casual%20clothing%2C%20professional%20family%20photography%2C%20simple%20background&width=400&height=300&seq=family-group-001&orientation=landscape"
                  alt="Multi-generational family"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg h-48 sm:h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=Colorful%20DNA%20double%20helix%20structure%20illustration%20with%20vibrant%20rainbow%20colors%20including%20pink%2C%20blue%2C%20green%2C%20yellow%2C%20and%20orange%2C%20modern%20scientific%20visualization%2C%20clean%20white%20background%2C%203D%20rendered%20genetic%20concept%20art&width=400&height=300&seq=dna-colorful-001&orientation=landscape"
                  alt="DNA structure"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg h-48 sm:h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=Happy%20extended%20family%20sitting%20together%20on%20white%20background%2C%20multiple%20generations%20including%20parents%2C%20children%2C%20and%20teenagers%2C%20casual%20comfortable%20clothing%2C%20warm%20smiles%2C%20professional%20studio%20family%20portrait%20photography&width=400&height=300&seq=family-sitting-001&orientation=landscape"
                  alt="Family sitting together"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg h-48 sm:h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=Joyful%20family%20of%20five%20walking%20hand%20in%20hand%20outdoors%20in%20sunny%20meadow%2C%20parents%20with%20three%20children%2C%20casual%20summer%20clothing%2C%20bright%20natural%20daylight%2C%20green%20grass%20field%2C%20professional%20lifestyle%20family%20photography&width=400&height=300&seq=family-walking-001&orientation=landscape"
                  alt="Family walking together"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Right - Content */}
            <div className="mt-8 lg:mt-0">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-gray-900 mb-6 sm:mb-8 leading-tight">
                What Is <strong className="font-semibold text-[#f04e58]">Familial Hypercholesterolemia?</strong>
              </h2>

              <div className="space-y-4 sm:space-y-6">
                <div>
                  <p className="text-base sm:text-lg text-gray-900 mb-2">
                    <strong className="font-semibold">Familial Hypercholesterolemia (FH)</strong> is a <strong className="font-semibold">genetic disorder</strong> that causes <strong className="font-semibold">very high levels of LDL cholesterol ("bad" cholesterol)</strong> from birth.
                  </p>
                </div>

                <div className="border-l-4 border-[#0d9488] pl-4 sm:pl-6">
                  <p className="text-base sm:text-lg text-gray-700 leading-relaxed font-light">
                    It's one of the most <strong className="font-semibold text-gray-900">common inherited metabolic conditions</strong>, and a <strong className="font-semibold text-gray-900">major cause of early heart disease</strong> if left untreated.
                  </p>
                </div>

                {/* Key Points */}
                <div className="mt-6 sm:mt-8 space-y-4">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#f04e58]/10 flex-shrink-0">
                      <i className="ri-alert-line text-lg sm:text-xl text-[#f04e58]"></i>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">High Risk</h3>
                      <p className="text-sm sm:text-base text-gray-600 font-light">Cholesterol builds up in arteries early, leading to heart attacks or stroke at young ages</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#f04e58]/10 flex-shrink-0">
                      <i className="ri-test-tube-line text-lg sm:text-xl text-[#f04e58]"></i>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">Genetic Cause</h3>
                      <p className="text-sm sm:text-base text-gray-600 font-light">Key genes involved: LDLR, APOB, and PCSK9</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#f04e58]/10 flex-shrink-0">
                      <i className="ri-group-line text-lg sm:text-xl text-[#f04e58]"></i>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">Common Condition</h3>
                      <p className="text-sm sm:text-base text-gray-600 font-light">Affects approximately 1 in 250 people worldwide</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding FH - Simple Animated Cards Section */}
      <section className="py-24 bg-gradient-to-br from-[#fef3f2] to-[#fde8e7]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
              Understanding <strong className="font-semibold text-[#f04e58]">Familial Hypercholesterolemia</strong>
            </h2>
            <p className="text-lg text-gray-700 font-light max-w-3xl mx-auto">
              Comprehensive information about causes, inheritance, symptoms, and treatment
            </p>
          </div>

          {/* Simple Animated Cards Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Card 1: What Causes FH */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#0d9488]/10 mb-6 flex-shrink-0">
                <i className="ri-dna-line text-2xl text-[#0d9488]"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">What Causes FH</h3>
              <p className="text-gray-700 mb-4 font-light leading-relaxed">
                FH is caused by mutations in genes responsible for clearing LDL cholesterol:
              </p>
              
              <div className="space-y-3 flex-grow">
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-600 font-light"><strong className="text-gray-900">LDLR gene</strong> - Makes LDL receptors</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-600 font-light"><strong className="text-gray-900">APOB gene</strong> - Helps LDL bind to receptors</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-600 font-light"><strong className="text-gray-900">PCSK9 gene</strong> - Regulates LDL receptors</p>
                </div>
              </div>
            </div>

            {/* Card 2: How It's Inherited */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-6 flex-shrink-0">
                <i className="ri-parent-line text-2xl text-[#f04e58]"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">How It's Inherited</h3>
              <p className="text-gray-700 mb-4 font-light leading-relaxed">
                FH follows an autosomal dominant pattern:
              </p>
              
              <div className="space-y-4 flex-grow">
                <div className="bg-[#0d9488]/5 rounded-xl p-4">
                  <p className="text-gray-900 font-semibold mb-1">50% chance</p>
                  <p className="text-sm text-gray-600 font-light">If one parent has FH</p>
                </div>
                <div className="bg-[#f04e58]/5 rounded-xl p-4">
                  <p className="text-gray-900 font-semibold mb-1">Severe form</p>
                  <p className="text-sm text-gray-600 font-light">If both parents have FH</p>
                </div>
              </div>
            </div>

            {/* Card 3: How Common Is It */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#0d9488]/10 mb-6 flex-shrink-0">
                <i className="ri-bar-chart-line text-2xl text-[#0d9488]"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">How Common Is It</h3>
              
              <div className="space-y-4 flex-grow">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-700 font-medium">Heterozygous FH</p>
                    <span className="text-lg font-bold text-[#0d9488]">1:250</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-[#0d9488] h-2 rounded-full" style={{ width: '40%' }}></div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-700 font-medium">Homozygous FH</p>
                    <span className="text-lg font-bold text-[#f04e58]">1:160K</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-[#f04e58] h-2 rounded-full" style={{ width: '5%' }}></div>
                  </div>
                </div>

                <div className="bg-[#f04e58]/10 rounded-xl p-3 mt-auto">
                  <p className="text-sm text-gray-900 font-semibold">&gt;90% remain undiagnosed</p>
                </div>
              </div>
            </div>

            {/* Card 4: Symptoms and Signs */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-6 flex-shrink-0">
                <i className="ri-eye-line text-2xl text-[#f04e58]"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Symptoms &amp; Signs</h3>
              <p className="text-gray-700 mb-4 font-light leading-relaxed">
                Physical clues that can appear:
              </p>
              
              <div className="space-y-3 flex-grow">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#f04e58] mt-0.5 flex-shrink-0"></i>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Xanthomas</p>
                    <p className="text-xs text-gray-600 font-light">Fatty lumps on tendons</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#f04e58] mt-0.5 flex-shrink-0"></i>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Xanthelasmas</p>
                    <p className="text-xs text-gray-600 font-light">Yellowish deposits around eyes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#f04e58] mt-0.5 flex-shrink-0"></i>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Corneal arcus</p>
                    <p className="text-xs text-gray-600 font-light">Grey-white ring around cornea</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 5: Why It's Serious */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-6 flex-shrink-0">
                <i className="ri-heart-pulse-line text-2xl text-[#f04e58]"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Why It's Serious</h3>
              <p className="text-gray-700 mb-4 font-light leading-relaxed">
                High LDL from birth causes early plaque buildup:
              </p>
              
              <div className="space-y-3 flex-grow">
                <div className="bg-gradient-to-r from-[#f04e58]/10 to-transparent rounded-lg p-3">
                  <p className="text-2xl font-bold text-[#f04e58]">2-4×</p>
                  <p className="text-sm text-gray-600 font-light">Higher LDL than normal</p>
                </div>
                <div className="bg-gradient-to-r from-[#f04e58]/10 to-transparent rounded-lg p-3">
                  <p className="text-2xl font-bold text-[#f04e58]">20×</p>
                  <p className="text-sm text-gray-600 font-light">Higher risk of heart disease</p>
                </div>
              </div>
            </div>

            {/* Card 6: Treatment */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#0d9488]/10 mb-6 flex-shrink-0">
                <i className="ri-medicine-bottle-line text-2xl text-[#0d9488]"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Treatment</h3>
              <p className="text-gray-700 mb-4 font-light leading-relaxed">
                FH is manageable with:
              </p>
              
              <div className="space-y-2 flex-grow">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#0d9488] text-white text-xs font-semibold flex-shrink-0">1</div>
                  <p className="text-sm text-gray-700">Statins</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#0d9488] text-white text-xs font-semibold flex-shrink-0">2</div>
                  <p className="text-sm text-gray-700">Ezetimibe</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#0d9488] text-white text-xs font-semibold flex-shrink-0">3</div>
                  <p className="text-sm text-gray-700">PCSK9 inhibitors</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#0d9488] text-white text-xs font-semibold flex-shrink-0">4</div>
                  <p className="text-sm text-gray-700">Lifestyle changes</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#0d9488] text-white text-xs font-semibold flex-shrink-0">5</div>
                  <p className="text-sm text-gray-700">Family screening</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero;
